import type {
  CreateTenantConfigDto,
  TenantConfigDto,
} from '../dto/tenantModel';
import {
  defaultAnalyticConfig,
  defaultEditProxyConfig,
  defaultServiceRate,
  defaultUnknownPersonConfig,
  DEFAULT_CRASH_RECORDING_DURATION_MINUTE,
  DEFAULT_CRASH_RECORDING_NAME,
  DEFAULT_FACE_DETECTION_MINIMUM_WIDTH_RATIO,
  defaultCustomIconRules
} from '../dto/tenantModel';
import { defaultShareLimits } from '../util/shareLimit';
import { paragraphRulesSettings, subtitleRulesSettings } from '../transcript/transcriptParametricRules';
import { TranscodingServiceProvider } from '../util/constants';
import cloneDeep from 'lodash/cloneDeep';
import type { FeatureFlag } from '../util/featureSet';

export const defaultCreateTenantConfig = (): CreateTenantConfigDto => cloneDeep({
    confidenceLevel: 90,
    faceDetectionMinimumWidthRatio: DEFAULT_FACE_DETECTION_MINIMUM_WIDTH_RATIO,
    unknownPersonDetectionConfig: defaultUnknownPersonConfig(),
    autoAddImagesToDetectedPersons: true,
    translationLanguage: 'en',
    secondaryTranslationLanguage: 'es',
    speakerDiarizationEnabled: false,
    autoTranslationEnabled: true,
    showPersonFunctionality: true,
    showAudioFunctionality: true,
    showFileFunctionality: true,
    showLabelFunctionality: true,
    showFolderFunctionality: true,
    transcodingService: TranscodingServiceProvider.MIMIR_MXCODER_PROXYGEN,
    paragraphParametricRules: paragraphRulesSettings,
    subtitleParametricRules: subtitleRulesSettings,
    forceCreateP2RootItem: false,
    isMoveDefault: false,
    analyticConfig: defaultAnalyticConfig(),
    conformAnalyticConfig: defaultAnalyticConfig(),
    recordingsAnalyticConfig: defaultAnalyticConfig(),
    editProxyConfig: defaultEditProxyConfig(),
    mdfTypeDefaults: {},
    serviceRate: defaultServiceRate(),
    crashRecordingDuration: DEFAULT_CRASH_RECORDING_DURATION_MINUTE,
    crashRecordingName: DEFAULT_CRASH_RECORDING_NAME,
    usePremierePreferenceForScaleToFrame: true,
    idleLogoutTimeoutMinutes: null,
    rekognitionAutoMigrate: false,
});

export const defaultTenantConfig = (): TenantConfigDto => cloneDeep({
  ...defaultCreateTenantConfig(),

  customActions: [],
  customActionsHeaders: [],
  customIcons: [],
  featureFlags: defaultFeatureFlags(),
  shareLimits: defaultShareLimits(),
  customIconRules: defaultCustomIconRules(),
  skippedFileExtensionsForPremiere: [],
});

export const defaultFeatureFlags = (): Record<FeatureFlag, boolean> => {
  return {
    trustUniqueNames: false,
    keldaStreaming: false,
    sequenceParts: false,
    itemDeduplication: false,
    playout: false,
    logClientHooks: false,
    ndiIngest: false,
    ndiPlayout: false,
    useKeldaTsLiveFolderIngester: false,
    subType: false,
    languageSelector: false,
    snowflake: false,
    archiveJobUpload: false,
    customSearchHook: false,
    keldaWatchFolderSegmentedProxy: false,
    useDefaultAudioLayoutInKeldaWatchFolderSegmentedProxy: false,
    prerenderFix: false,
    replication: false,
    keldaHighresJobUpload: false,
    useCacheForBilling: false,
    meaImport: false,
    keldaUsePrimaryRootSubfolders: false,
    stopUsingMimirServiceAccountForRClone: false,
    directRenderToEquivalentPath: false,
    disableArbitraryWildcardSupport: false,
    disableSearchFilterParsingV2: false,
    enableConsumptionBasedMetering: false,
    clientHookForTimedMetadata: false,
    remuxTsToMp4InPremiere: false,
    useFasterTranscodingPresets: false,
    useNewBufferingLogic: false,
    soloingAudioTracks: false,
    enableHasRelationsSearch: false,
    userTasks: false,
    getItemLegacyFields: false,
    showSetFiltersAsDefault: false,
    preferEc2Transcoding: false,
    enforceDistinctEc2Instance: false,
    fallbackToFargateTranscoding: false,
    showFolderMetadataEditor: false,
    disableCreateCutterSequenceNotification: false,
    allowEgressDuringArchive: false,
    batchProcessKelda: false,
    concurrentMirrorModeFileHandling: false,
    newTabsAndCutter: false,
    enableSectionAnchors: false,
    enhancedShare: false,
    noRelationsSizeLimit: false,
    disableVdcpStatus: false,
    replaceRekognitionPersonDetection: false,
    bookmarkV2: false,
    pinning: false,
    replaceRekognitionFaceSearch: false,
    createSequenceItemAndDependencyInSameFolder: false,
    sentry: false,
    allowSettingCreatedOn: false,
    keldaEndToEndTranscoding: false,
    useRootLevelMediaCreatedForSorting: false,
    forceFacePickerV7Primary: false,
    dualWriteFaceCollection: false,
    forceFaceMigration: false,
    doNotSetDefaultTimedMetadataValuesOnCreate: false,
    rekognitionAutoMigrate: false,
    disableOldCutter: false,
    useAggregatedFieldSearch: false,
    commandPalette: false,
    mobileLive: false,
    localFolderView: false,
    useCutterPlayer: false,
    aclAwareFolderDelete: false,
    killClosedCaptionCollection: false,
    killMultiChannelClosedCaptions: false,
    preventNestedQuickPreview: false,
    useGraphics: false,
    disableDeprecatedItemAnalyticsSearchAPI: false,
    periodicallyUpdateRouterSource: true,
    editSharedTranscript: false,
    useNewOrchestrationForCentralConform: false,
    useNewOrchestrationForCutterRenderSequence: false,
    offlineStoragePlugin: false,
    wsBroadcastViaQueue: false,
    distributedRendering: false,
    disableInconsistentSegmentDurationWarning: false,
    dropMetadataAndOthersSupportItemRenderApi: false,
    renderScaler: false,
    newProxyConfig: false,
    keldaUseClaimerWorkers: false,
  };
};
